import React, { useState, useEffect } from 'react';
import { auth } from '../../firebase';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  signInWithEmailAndPassword,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  sendResetPasswordEmailApi,
  approveEduAfterSetPasswordApi,
} from '../../services/firebaseApiService';
import '../../styles/LoginPopup.css';

/** Match App.js — open Fox Chat after email-link sign-in landed on this path by mistake. */
const POST_LOGIN_COLLAGES_SESSION_KEY = 'luvwtf_postLoginCollages';

const SetPasswordPage = () => {
  const params = new URLSearchParams(window.location.search);
  const oobCode = params.get('oobCode');
  const mode = params.get('mode');

  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalidLink, setInvalidLink] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitDots, setSubmitDots] = useState(1);
  const [success, setSuccess] = useState(false);
  const [manualEmail, setManualEmail] = useState('');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  /** True after confirmPasswordReset fails with expired/invalid oobCode — show “Send a new link”. */
  const [showNewLinkCta, setShowNewLinkCta] = useState(false);

  /* Cycle "Setting password." / ".." / "..." while the request is in flight. */
  useEffect(() => {
    if (!isSubmitting) {
      setSubmitDots(1);
      return undefined;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setSubmitDots(3);
      return undefined;
    }
    const id = window.setInterval(() => {
      setSubmitDots((n) => (n % 3) + 1);
    }, 400);
    return () => window.clearInterval(id);
  }, [isSubmitting]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const sendNewLinkTo = async (address) => {
    const trimmed = address.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setResendError('Enter a valid email address.');
      return;
    }
    if (resendCooldown > 0 || resendBusy) return;
    setResendError('');
    setResendSent(false);
    setResendBusy(true);
    try {
      try {
        window.localStorage.setItem('emailForSignIn', trimmed.toLowerCase());
      } catch (_) {}
      await sendResetPasswordEmailApi(trimmed);
      setResendSent(true);
      setResendCooldown(60);
    } catch (err) {
      setResendError(err.message || 'Could not send. Try again.');
    } finally {
      setResendBusy(false);
    }
  };

  useEffect(() => {
    if (mode === 'resetPassword' && oobCode) {
      const verify = async () => {
        try {
          const emailFromCode = await verifyPasswordResetCode(auth, oobCode);
          setEmail(emailFromCode);
        } catch (err) {
          console.error('Invalid or expired reset code:', err);
          setInvalidLink(true);
        } finally {
          setLoading(false);
        }
      };

      verify();
      return undefined;
    }

    // Missing reset query: bad link, or email-link sign-in stripped oobCode but left /set-password.
    let cancelled = false;
    const unsub = onAuthStateChanged(auth, () => {
      unsub();
      if (cancelled) return;
      const u = auth.currentUser;
      if (u && !u.isAnonymous && u.email) {
        try {
          sessionStorage.setItem(POST_LOGIN_COLLAGES_SESSION_KEY, '1');
        } catch (_) {}
        window.location.replace('/');
        return;
      }
      setInvalidLink(true);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, [mode, oobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setShowNewLinkCta(false);
    setIsSubmitting(true);

    try {
      await confirmPasswordReset(auth, oobCode, password);
      await signInWithEmailAndPassword(auth, email, password);
      /* Completing the reset link proves inbox ownership; .edu account emails get the
         profile approved here instead of via a separate verify-email step. */
      try {
        await approveEduAfterSetPasswordApi();
      } catch (approveErr) {
        console.warn('Post set-password .edu approval failed:', approveErr?.message || approveErr);
      }
      setSuccess(true);
      setTimeout(() => {
        try {
          sessionStorage.setItem('luvwtf_postLoginCollages', '1');
        } catch (_) {}
        window.location.href = '/';
      }, 1500);
    } catch (err) {
      console.error('Error setting password:', err);
      if (err.code === 'auth/expired-action-code') {
        setShowNewLinkCta(true);
        setError('This link has expired. You can send yourself a new link below.');
      } else if (err.code === 'auth/invalid-action-code') {
        setShowNewLinkCta(true);
        setError('This link is invalid or has already been used. You can send yourself a new link below.');
      } else {
        setShowNewLinkCta(false);
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="login-popup-overlay">
        <div className="login-popup">
          <div className="login-popup-header">
            <h2>Loading...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (invalidLink) {
    const canResend = resendCooldown <= 0 && !resendBusy;
    return (
      <div className="login-popup-overlay">
        <div className="login-popup">
          <div className="login-popup-header">
            <h2>Invalid or missing link</h2>
          </div>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '1rem' }}>
            This link is invalid or has expired. Enter your email and we&apos;ll send a fresh link.
          </p>
          <div className="form-group" style={{ textAlign: 'center' }}>
            <label htmlFor="resend-email-invalid">Email</label>
            <input
              type="email"
              id="resend-email-invalid"
              value={manualEmail}
              onChange={(e) => setManualEmail(e.target.value)}
              placeholder="you@berkeley.edu"
              autoComplete="email"
            />
          </div>
          <button
            type="button"
            className="login-button"
            style={{ maxWidth: '220px', margin: '1.5rem auto 1rem', display: 'block' }}
            disabled={!canResend}
            onClick={() => sendNewLinkTo(manualEmail)}
          >
            {resendBusy
              ? 'Sending…'
              : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : 'Send a new link'}
          </button>
          {resendSent && (
            <p style={{ textAlign: 'center', color: '#16a34a', fontSize: '14px', marginBottom: '1rem' }}>
              Check your inbox and spam for the new link.
            </p>
          )}
          {resendError && (
            <div className="error-message" style={{ textAlign: 'center', marginBottom: '1rem' }}>
              {resendError}
            </div>
          )}
          <a href="/" className="signup-link" style={{ textAlign: 'center', display: 'block' }}>
            Back to home
          </a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-popup-overlay">
        <div className="login-popup">
          <div className="login-popup-header">
            <h2>Password set!</h2>
          </div>
          <p style={{ textAlign: 'center', color: '#666' }}>
            Redirecting you to the app...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-popup-overlay">
      <div className="login-popup">
        <div className="login-popup-header" style={{ marginBottom: '1rem' }}>
          <h2>Set your password</h2>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ textAlign: 'center' }}>
            <label htmlFor="password">New password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              required
              minLength={6}
            />
          </div>
          <div className="form-group" style={{ textAlign: 'center' }}>
            <label htmlFor="confirm-password">Confirm password</label>
            <input
              type="password"
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              required
              minLength={6}
            />
          </div>
          {error && (
            <div className="error-message" style={{ color: '#ef4444', marginBottom: '1rem', textAlign: 'center' }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            className="login-button"
            style={{ maxWidth: '180px', margin: '0 auto', display: 'block' }}
            disabled={isSubmitting || !password || !confirmPassword}
            aria-busy={isSubmitting}
            aria-label={isSubmitting ? 'Setting password' : undefined}
          >
            {isSubmitting ? (
              <>
                Setting password
                <span className="signup-submitting-dots" aria-hidden="true">
                  {'.'.repeat(submitDots)}
                </span>
              </>
            ) : (
              'CONFIRM'
            )}
          </button>
          {showNewLinkCta && email && (
            <button
              type="button"
              className="login-button"
              style={{
                maxWidth: '220px',
                margin: '1rem auto 0',
                display: 'block',
                background: '#444',
              }}
              disabled={resendCooldown > 0 || resendBusy}
              onClick={() => sendNewLinkTo(email)}
            >
              {resendBusy
                ? 'Sending…'
                : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : 'Send a new link'}
            </button>
          )}
          {resendSent && email && (
            <p style={{ textAlign: 'center', color: '#16a34a', fontSize: '14px', marginBottom: '1rem' }}>
              Check your inbox and spam for the new link.
            </p>
          )}
          {resendError && email && (
            <div className="error-message" style={{ textAlign: 'center', marginBottom: '1rem' }}>
              {resendError}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default SetPasswordPage;
